import * as React from 'react'

import { Info } from 'lucide-react'
import { observer } from 'mobx-react'

import { RestrictionType } from '~/client/graph'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import DateClosuresCell from './cells/DateClosuresCell'
import {
  EditableTagsCellReadOnlyContext,
  EditableTagsCellVariantContext,
} from './cells/EditableTagsCell'
import HoursOfOperationCell from './cells/HoursOfOperationCell'
import PermittedCompaniesCell from './cells/PermittedCompaniesCell'
import WorkflowRestrictionCell from './cells/WorkflowRestrictionCell'
import WorkflowTypesCell from './cells/WorkflowTypesCell'
import {
  DATE_CLOSURES_KEY,
  HOURS_OF_OPERATION_KEY,
  MAX_BOOKING_DURATION_KEY,
  MAX_OVERLAPPING_KEY,
  PERMITTED_COMPANIES_KEY,
  SHOW_IN_MAP_KEY,
  WORKFLOW_TYPES_KEY,
} from './controlKeys'
import { useResourceControlsTab } from './ResourceControlsTabContext'

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="tw:flex tw:flex-col tw:gap-2">
    <span className="tw:text-body-md tw:font-medium tw:text-foreground">
      {label}
    </span>
    {children}
  </div>
)

/**
 * Controls tab body for a single resource. The WR (Workflow Resource) switch sits
 * at the top; when it is on, the resource's configured controls render below as a
 * vertical form (reusing the controls-table editor cells in their bordered variant).
 * Which controls show is driven by `controls` (Equipment: all; Materials: workflow
 * types + permitted companies). Edits update a local draft; Save/Cancel live in the
 * panel footer.
 */
function ResourceControlsTab() {
  const ctx = useResourceControlsTab()
  const { translator } = Localization
  const has = (key: string) => ctx.controls.includes(key)

  // The card shows the full "Workflow Resource" label, so its tooltip drops the
  // shared string's leading "WR (Workflow Resource):" heading and shows only the
  // On/Off meaning — one source of truth, no duplicated copy.
  const workflowResourceStateTooltip = translator.workflowResourceTooltip
    .split('\n')
    .slice(1)
    .join('\n')

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {/* Leads the section: an independent display flag, not gated by the WR toggle, so
          it sits outside the isBookable block below. */}
      {has(SHOW_IN_MAP_KEY) && (
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <span className="tw:text-body-md tw:font-medium tw:text-foreground">
            {translator.showInMap}
          </span>
          <Switch
            checked={ctx.showInMap}
            onCheckedChange={ctx.setShowInMap}
            disabled={ctx.readOnly}
            aria-label={translator.showInMap}
          />
        </div>
      )}

      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:text-body-md tw:font-medium tw:text-foreground">
          {translator.workflowResourceBulk}
          <Tooltip>
            <TooltipTrigger
              type="button"
              aria-label={workflowResourceStateTooltip}
              className="tw:inline-flex tw:cursor-default tw:items-center tw:text-muted-foreground tw:hover:text-foreground"
            >
              <Info className="tw:size-3.5" />
            </TooltipTrigger>
            <TooltipContent className="tw:max-w-[280px] tw:whitespace-pre-line">
              {workflowResourceStateTooltip}
            </TooltipContent>
          </Tooltip>
        </span>
        {/* The WR switch is the one control that isn't a tag-picker, so it can't
            ride the EditableTagsCellReadOnlyContext choke point below — disable it
            explicitly for members. */}
        <Switch
          checked={ctx.isBookable}
          onCheckedChange={ctx.setIsBookable}
          disabled={ctx.readOnly}
          aria-label={translator.workflowResourceBulk}
        />
      </div>

      {ctx.isBookable && (
        <EditableTagsCellVariantContext.Provider value="bordered">
          <EditableTagsCellReadOnlyContext.Provider value={ctx.readOnly}>
            <div className="tw:flex tw:flex-col tw:gap-4">
              {has(WORKFLOW_TYPES_KEY) && (
                <Field label={translator.workflowTypes}>
                  <WorkflowTypesCell
                    isBookable={ctx.isBookable}
                    isSavedBookable={ctx.savedIsBookable}
                    onAssign={ctx.enableNow}
                    presentation="sheet"
                    resourceTypeId={ctx.resourceTypeId}
                    recordId={ctx.recordId}
                  />
                </Field>
              )}

              {has(PERMITTED_COMPANIES_KEY) && (
                <Field label={translator.permittedCompanies}>
                  <PermittedCompaniesCell
                    companyIds={ctx.permittedCompanyIds}
                    companies={ctx.companies}
                    companyMetadata={ctx.companyMetadata}
                    isBookable={ctx.isBookable}
                    getCompanyById={ctx.getCompanyById}
                    onChange={ctx.setPermittedCompanyIds}
                    presentation="sheet"
                  />
                </Field>
              )}

              {has(HOURS_OF_OPERATION_KEY) && (
                <Field label={translator.hoursOfOperation}>
                  <HoursOfOperationCell
                    intervals={ctx.operatingIntervals}
                    projectDateStore={ctx.projectDateStore}
                    isBookable={ctx.isBookable}
                    onChange={ctx.setOperatingIntervals}
                    onEditProjectHours={ctx.onEditProjectHours}
                    isMobile={ctx.isMobile}
                    presentation="sheet"
                  />
                </Field>
              )}

              {has(DATE_CLOSURES_KEY) && (
                <Field label={translator.dateClosures}>
                  <DateClosuresCell
                    closures={ctx.closures}
                    projectDateStore={ctx.projectDateStore}
                    isBookable={ctx.isBookable}
                    onSave={ctx.saveClosure}
                    onRemove={ctx.removeClosure}
                    presentation="sheet"
                    inheritedClosures={ctx.inheritedClosures}
                    onEditProjectClosures={ctx.onEditProjectHours}
                    isMobile={ctx.isMobile}
                  />
                </Field>
              )}

              {has(MAX_BOOKING_DURATION_KEY) && (
                <Field label={translator.maxBookingDuration}>
                  <WorkflowRestrictionCell
                    recordId={ctx.recordId}
                    restrictions={ctx.getRestrictions(
                      RestrictionType.MaxDuration,
                    )}
                    restrictionType={RestrictionType.MaxDuration}
                    label={translator.maxBookingDuration}
                    allWorkflowTypeIds={ctx.allWorkflowTypeIds}
                    isBookable={ctx.isBookable}
                    getPermitTypeById={ctx.getPermitTypeById}
                    onSave={(_recordId, drafts) =>
                      ctx.upsertRestrictions(drafts)
                    }
                    onDelete={ctx.deleteRestrictions}
                    presentation="sheet"
                  />
                </Field>
              )}

              {has(MAX_OVERLAPPING_KEY) && (
                <Field label={translator.maxOverlapping}>
                  <WorkflowRestrictionCell
                    recordId={ctx.recordId}
                    restrictions={ctx.getRestrictions(
                      RestrictionType.MaxOverlapping,
                    )}
                    restrictionType={RestrictionType.MaxOverlapping}
                    label={translator.maxOverlapping}
                    allWorkflowTypeIds={ctx.allWorkflowTypeIds}
                    isBookable={ctx.isBookable}
                    getPermitTypeById={ctx.getPermitTypeById}
                    onSave={(_recordId, drafts) =>
                      ctx.upsertRestrictions(drafts)
                    }
                    onDelete={ctx.deleteRestrictions}
                    presentation="sheet"
                  />
                </Field>
              )}
            </div>
          </EditableTagsCellReadOnlyContext.Provider>
        </EditableTagsCellVariantContext.Provider>
      )}
    </div>
  )
}

export default observer(ResourceControlsTab)
